import React, {useContext} from "react";
import { PERMISSIONS, useUser } from "./UserContext";

const defaultValue = {
    title: '',
    component: (<div></div>),
    open: false,
    openId: 0,
    // Tam ekran artık kullanıcı tercihi (kalıcı toggle) DEĞİL: yalnız openFullScreenModal ile
    // açılan ekranlar tam ekrandır. Bkz. components/Modal.js
    fullScreen: false,
    modal2: {
        open: false,
        component: null,
        title: ''
    }
}

export const ModalActions = {
    OPEN_MODAL: 'OPEN_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL',
    SET_MODAL_TITLE: 'SET_MODAL_TITLE',
}

const ModalContext = React.createContext(defaultValue)

function modalReducer(state, action)
{
    switch (action.type)
    {
        case ModalActions.OPEN_MODAL: {
            // Her açılışta artan kimlik: Modal.js'te key olarak kullanılıp içeriğin
            // taze mount olmasını (bayat component sorunu) sağlar; setTimeout hack'i kaldırıldı.
            const openId = (state.openId ?? 0) + 1;

            if(state.open)
            {
                // Üstteki (ikincil) modal hiçbir zaman tam ekran değildir: altındaki modalı
                // tamamen örtmesi kullanıcıyı bağlamdan koparır.
                return {
                    ...state,
                    openId,
                    modal2: {
                        open: true,
                        component: action.component,
                        title: action.title,
                        onCloseFn: action.onCloseFn,
                    }
                }
            }

            return {
                ...state,
                openId,
                open: true,
                component: action.component,
                title: action.title,
                onCloseFn: action.onCloseFn,
                width: action.width,
                top: action.top,
                fullScreen: !!action.fullScreen,
            }
        }
        case ModalActions.SET_MODAL_TITLE: {
            return {
                ...state,
                title: action.title,
            }
        }
        case ModalActions.CLOSE_MODAL: {
            // typeof kontrolü ŞART: openModal konumsal argüman alıyor (title, component, onCloseFn, …).
            // Çağıran 3. sıraya yanlışlıkla bir seçenek nesnesi geçerse truthy olur ve çağrıldığında
            // "onCloseFn is not a function" ile TÜM uygulamayı düşürürdü. Kapanış yolu bunu yutmamalı.
            // skipOnClose: ekran hiç açılmadan reddedildi (ör. rapor kilidi); açanın kapanış işlevi (grid tazeleme) çalışmaz.
            if(state.modal2.open)
            {
                if (!action.skipOnClose && typeof state.modal2.onCloseFn === 'function')
                    state.modal2.onCloseFn()

                return {
                    ...state,
                    modal2: defaultValue.modal2
                }
            }

            if (!action.skipOnClose && typeof state.onCloseFn === 'function')
                state.onCloseFn()

            return defaultValue
        }
        default: {
            return defaultValue
        }
    }
}

function ModalProvider({children})
{
    const [modal, dispatch] = React.useReducer(modalReducer, defaultValue);

    const value = {modal, dispatch};

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    )
}

function useModal()
{
    const context = React.useContext(ModalContext);

    if(!context)
        throw new Error('Modal Provider Error')

    const {modal, dispatch} = context;

    const openModal = (title, component, onCloseFn, width, top) => {
        dispatch({
            type: ModalActions.OPEN_MODAL,
            component: component,
            title: title,
            width,
            top,
            onCloseFn
        })
    }

    /**
     * Tam ekran açılan modal. Eskiden her modalın başlığında bir tam ekran düğmesi vardı ve
     * seçim localStorage'da tutulup TÜM modallara uygulanıyordu; artık tam ekran yalnız burayı
     * çağıran ekranlara özel. width/top geçilmez — tam ekranda anlamları yok.
     */
    const openFullScreenModal = (title, component, onCloseFn) => {
        dispatch({
            type: ModalActions.OPEN_MODAL,
            component: component,
            title: title,
            onCloseFn,
            fullScreen: true
        })
    }

    // options.skipOnClose: açanın kapanış işlevi çalışmaz (bkz. reducer). Düğmeye doğrudan bağlanınca
    // olay nesnesi gelir; yalnız açıkça true verilirse atlanır.
    const closeModal = (options) => {
        dispatch({
            type: ModalActions.CLOSE_MODAL,
            skipOnClose: options?.skipOnClose === true,
        })
    }

    // Açık modalın başlığını (string veya component) sonradan güncelle.
    const setModalTitle = (title) => {
        dispatch({
            type: ModalActions.SET_MODAL_TITLE,
            title,
        })
    }

    return {
        modal: modal,
        openModal,
        openFullScreenModal,
        closeModal,
        setModalTitle
    }
}

export {ModalProvider, useModal}
